// src/pages/PricingPage.tsx
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PricingCard from '../components/pricing/PricingCard';

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [highlightedPlan, setHighlightedPlan] = useState<string | null>(null);

  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan) {
      setHighlightedPlan(plan);
      setTimeout(() => {
        const element = document.getElementById('pricing-cards');
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [searchParams]);
const pricingPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 29,
      carrierLimit: '20 carriers',
      features: [
        'Up to 20 carriers',
        'Carrier onboarding & compliance',
        'Document collection & storage',
        'Approve/reject workflow',
        'Insurance expiration tracking',
        'Rate confirmations & e-signatures',
        'Carrier compliance dashboard',
        'Email support'
      ],
      isPopular: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 79,
      carrierLimit: '50 carriers',
      features: [
        'Up to 50 carriers',
        'Carrier onboarding & compliance',
        'Document collection & storage',
        'Approve/reject workflow',
        'Insurance expiration tracking',
        'Rate confirmations & e-signatures',
        'Carrier compliance dashboard',
        'Priority support'
      ],
      isPopular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 199,
      carrierLimit: 'Unlimited carriers',
      features: [
        'Unlimited carriers',
        'Carrier onboarding & compliance',
        'Document collection & storage',
        'Approve/reject workflow',
        'Insurance expiration tracking',
        'Rate confirmations & e-signatures',
        'Carrier compliance dashboard',
        'Dedicated account manager'
      ],
      isPopular: false
    }
  ];

  return (
    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Pricing Plans</h1>
          <p className="text-xl text-gray-600 mb-2">
            Choose the plan that fits your brokerage
          </p>
          <p className="text-sm text-gray-500">
            Start with a 7-day free trial. No credit card required. Or buy now and skip the trial.
          </p>
    
        </div>

        <div id="pricing-cards" className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              name={plan.name}
              price={plan.price}
              carrierLimit={plan.carrierLimit}
              features={plan.features}
              isPopular={plan.isPopular}
              isRecommended={plan.id === highlightedPlan}
            />
          ))}
        </div>

        {/* All Plans Include Section */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
            All Plans Include
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Document Management</h3>
                <p className="text-sm text-gray-600">Collect, store, and organize COI, W9, and authority documents</p>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Review & Approval</h3>
                <p className="text-sm text-gray-600">Approve or reject carrier documents with notes and timestamps</p>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Role-Based Dashboards</h3>
                <p className="text-sm text-gray-600">Separate portals for brokers and carriers</p>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Insurance Tracking</h3>
                <p className="text-sm text-gray-600">Automatic insurance expiration alerts and compliance monitoring</p>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Rate Confirmations</h3>
                <p className="text-sm text-gray-600">Send, sign, and manage rate cons with electronic signatures</p>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <h3 className="font-semibold text-gray-900">Broker Data Isolation</h3>
                <p className="text-sm text-gray-600">Your carrier data stays private and separated from other brokers</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ or CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Need help choosing? Contact us for a personalized recommendation.
        </p>
          <a
            href="/contact"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;